import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface QRScannerModalProps {
  isOpen: boolean;
  stream: MediaStream | null;
  isProcessing: boolean;
  onClose: () => void;
  onScan: () => void;
}

export const QRScannerModal: React.FC<QRScannerModalProps> = ({
  isOpen,
  stream,
  isProcessing,
  onClose,
  onScan
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [isOpen, stream]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex justify-between items-center p-4 bg-black text-white">
        <h2 className="text-lg font-semibold">Scan QR Code</h2>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-800 transition-colors">
          <X className="w-6 h-6" />
        </button>
      </div>
      
      <div className="flex-1 flex items-center justify-center relative">
        {stream && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        )}
        
        {/* QR Scanner Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-64 h-64 border-4 border-white border-opacity-50 rounded-lg relative">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg"></div>
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg"></div>
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg"></div>
          </div>
        </div>
        
        {isProcessing && (
          <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 text-center">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-800 font-medium">Processing QR Code...</p>
              <p className="text-sm text-gray-600">Reading QR content</p>
            </div>
          </div>
        )}
      </div>
      
      <div className="p-4 bg-black text-white">
        <div className="text-center mb-4">
          <p className="text-sm text-gray-300 mb-2">Position any QR code within the frame</p>
          <p className="text-xs text-gray-400">Complete QR content will be saved</p>
        </div>
        
        <button
          onClick={onScan}
          disabled={isProcessing}
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            isProcessing
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isProcessing ? 'Processing...' : 'Scan QR Code'}
        </button>
      </div>
    </div>
  );
};

export default QRScannerModal;
